// trafficLightTool.js
import * as Cesium from "cesium";
import { getUsernameAndUserIdFormat } from "../Utility";

let trafficLightModeEnabled = false;
let trafficLightHandler = null;
let trafficLightEscHandler = null;

function getNumericPropertyId(entity) {
  try {
    const prop = entity?.properties?.id;
    const value = prop && typeof prop.getValue === "function" ? prop.getValue(Cesium.JulianDate.now()) : prop;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function collectIdsFromGeoJSON(gjson, out = new Set()) {
  if (!gjson?.features || !Array.isArray(gjson.features)) return out;

  for (const f of gjson.features) {
    const n = Number(f?.properties?.id);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
}

function collectIdsFromDataSource(dataSource, out = new Set()) {
  const entities = dataSource?.entities?.values || [];

  for (const entity of entities) {
    const n = getNumericPropertyId(entity);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
}

function normalizeLayerIdentity(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");
}

function findMapObjectByDataSource(dataSource, mapObjectsRef) {
  const list = Array.isArray(mapObjectsRef?.current) ? mapObjectsRef.current : [];

  return (
    list.find((o) => {
      if (!o || !dataSource) return false;
      if (o.dataSource === dataSource || o.laneletDataSource === dataSource) return true;
      if (dataSource.layerId != null && o.layer_id != null && String(o.layer_id) === String(dataSource.layerId)) return true;

      const a = normalizeLayerIdentity(o.file_name || o.layer_name || o.name);
      const b = normalizeLayerIdentity(dataSource.layerName || dataSource.name);
      return a && b && a === b;
    }) || null
  );
}

function getNextIdFromDataSource(dataSource, mapObjectsRef = null, setMapObjects = null) {
  const layer = findMapObjectByDataSource(dataSource, mapObjectsRef);
  const ids = new Set();

  for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson]) {
    collectIdsFromGeoJSON(gjson, ids);
  }

  collectIdsFromDataSource(dataSource, ids);

  const stored = Number(layer?.bboxMaxFeaturePropertiesId ?? layer?.bboxMaxId);
  const lastId = Number(layer?.geojson_last_id);

  let maxId = -2;
  if (Number.isFinite(stored)) maxId = Math.max(maxId, stored);
  if (Number.isFinite(lastId)) maxId = Math.max(maxId, lastId);

  for (const id of ids) {
    if (id > maxId) maxId = id;
  }

  let nextId = maxId + 2;
  while (ids.has(nextId)) {
    nextId += 2;
  }

  if (layer) {
    layer.geojson_last_id = nextId;
    layer.bboxMaxId = nextId;
    layer.bboxMaxFeaturePropertiesId = nextId;
  }

  const patch = (o) => {
    if (!o || !dataSource) return o;

    const sameLayer =
      o === layer ||
      o.dataSource === dataSource ||
      o.laneletDataSource === dataSource ||
      (dataSource.layerId != null && o.layer_id != null && String(o.layer_id) === String(dataSource.layerId)) ||
      (normalizeLayerIdentity(o.file_name || o.layer_name || o.name) && normalizeLayerIdentity(o.file_name || o.layer_name || o.name) === normalizeLayerIdentity(dataSource.layerName || dataSource.name));

    if (!sameLayer) return o;

    return {
      ...o,
      geojson_last_id: nextId,
      bboxMaxId: nextId,
      bboxMaxFeaturePropertiesId: nextId,
    };
  };

  if (Array.isArray(mapObjectsRef?.current)) {
    mapObjectsRef.current = mapObjectsRef.current.map(patch);
  }

  if (typeof setMapObjects === "function") {
    setMapObjects((prev) => (Array.isArray(prev) ? prev.map(patch) : prev));
  }

  return nextId;
}
// viewer: Cesium.Viewer
// onCreateFeatures: (polygonFeature | null, pointFeature | null) => void
// destroy: boolean (if true => only destroy, if false => init/enable)
// lightDataSource: DataSource for polygon
// poleDataSource:  DataSource for point
export function toggleTrafficLightTool(viewer, onCreateFeatures, destroy = false, lightDataSource = null, poleDataSource = null, userData, mapObjectsRef = null, setMapObjects = null) {
  if (!viewer) return false;

  // destroy mode
  if (destroy === true) {
    trafficLightModeEnabled = false;
    if (trafficLightHandler) {
      trafficLightHandler.destroy();
      trafficLightHandler = null;
    }
    if (trafficLightEscHandler) {
      window.removeEventListener("keydown", trafficLightEscHandler, { capture: true });
      trafficLightEscHandler = null;
    }

    return false;
  }

  // already enabled
  if (trafficLightModeEnabled && trafficLightHandler) {
    return true;
  }

  trafficLightModeEnabled = true;
  trafficLightHandler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  const creator = getUsernameAndUserIdFormat(userData);

  trafficLightEscHandler = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();

    trafficLightModeEnabled = false;

    if (trafficLightHandler) {
      trafficLightHandler.destroy();
      trafficLightHandler = null;
    }

    if (trafficLightEscHandler) {
      window.removeEventListener("keydown", trafficLightEscHandler, { capture: true });
      trafficLightEscHandler = null;
    }
  };

  window.addEventListener("keydown", trafficLightEscHandler, { capture: true });

  trafficLightHandler.setInputAction(function (click) {
    if (!trafficLightModeEnabled) return;

    const scene = viewer.scene;
    const ellipsoid = Cesium.Ellipsoid.WGS84;

    // pick ground position
    let cartesian = scene.pickPosition(click.position);
    if (!cartesian) {
      cartesian = viewer.camera.pickEllipsoid(click.position, ellipsoid);
    }
    if (!cartesian) return;

    // local ENU frame
    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(cartesian);
    const east = Cesium.Matrix4.getColumn(enu, 0, new Cesium.Cartesian3());
    const north = Cesium.Matrix4.getColumn(enu, 1, new Cesium.Cartesian3());
    Cesium.Cartesian3.normalize(east, east);
    Cesium.Cartesian3.normalize(north, north);

    function offsetFrom(origin, eastMeters, northMeters) {
      const eastOffset = Cesium.Cartesian3.multiplyByScalar(east, eastMeters, new Cesium.Cartesian3());
      const northOffset = Cesium.Cartesian3.multiplyByScalar(north, northMeters, new Cesium.Cartesian3());
      const tmp = Cesium.Cartesian3.add(origin, eastOffset, new Cesium.Cartesian3());
      return Cesium.Cartesian3.add(tmp, northOffset, new Cesium.Cartesian3());
    }

    function toLonLat(pos) {
      const c = ellipsoid.cartesianToCartographic(pos);
      return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
    }

    // rectangle 1.0m x 0.5m
    const halfLong = 1.0 / 2.0;
    const halfShort = 0.5 / 2.0;

    const topLeft = offsetFrom(cartesian, -halfLong, halfShort);
    const topRight = offsetFrom(cartesian, halfLong, halfShort);
    const bottomRight = offsetFrom(cartesian, halfLong, -halfShort);
    const bottomLeft = offsetFrom(cartesian, -halfLong, -halfShort);

    const tl = toLonLat(topLeft);
    const tr = toLonLat(topRight);
    const br = toLonLat(bottomRight);
    const bl = toLonLat(bottomLeft);

    let polygonEntity = null;
    let polygonFeature = null;
    let pointEntity = null;
    let pointFeature = null;

    // point position is needed regardless (for feature)
    const topMid = offsetFrom(cartesian, 0, halfShort);
    const pointCart = offsetFrom(topMid, 0, 0.5);
    const pointLonLat = toLonLat(pointCart);

    // --------------------------
    // point / pole
    // --------------------------
    let poleId = null;
    if (poleDataSource) {
      const entities = poleDataSource.entities;
      const all = entities.values;
      const last = all.length > 0 ? all[all.length - 1] : null;

           poleId = getNextIdFromDataSource(poleDataSource, mapObjectsRef, setMapObjects);

      // fallback style
      let pointOptions = {
        pixelSize: 10,
        color: Cesium.Color.RED,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      };

      // copy style from last point entity if exists
      if (last && last.point) {
        const p = last.point;
        pointOptions = {
          pixelSize: p.pixelSize,
          color: p.color,
          outlineColor: p.outlineColor,
          outlineWidth: p.outlineWidth,
          heightReference: p.heightReference,
          disableDepthTestDistance: p.disableDepthTestDistance,
        };
      }

      pointEntity = entities.add({
        position: Cesium.Cartesian3.fromDegrees(pointLonLat[0], pointLonLat[1]),
        point: pointOptions,
        properties: new Cesium.PropertyBag({
          id: new Cesium.ConstantProperty(poleId),
          ...(creator && {
            creator: new Cesium.ConstantProperty(creator),
          }),
        }),
      });

      pointFeature = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [pointLonLat[0], pointLonLat[1]],
        },
        properties: {
          kind: "traffic_light_point",
          entityId: pointEntity.id,
          id: poleId,
        },
      };
    }

    // --------------------------
    // polygon / light
    // --------------------------
    let polygonId = null;
    if (lightDataSource) {
      const entities = lightDataSource.entities;
      const all = entities.values;
      const last = all.length > 0 ? all[all.length - 1] : null;

            polygonId = getNextIdFromDataSource(lightDataSource, mapObjectsRef, setMapObjects);

      // default polygon style
      let polygonOptions = {
        hierarchy: Cesium.Cartesian3.fromDegreesArray([tl[0], tl[1], tr[0], tr[1], br[0], br[1], bl[0], bl[1]]),
        fill: false,
        outline: true,
        outlineColor: Cesium.Color.YELLOW,
        outlineWidth: 2.0,
        // heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
      };

      // copy style from last polygon entity if exists
      if (last && last.polygon) {
        const p = last.polygon;
        polygonOptions = {
          hierarchy: Cesium.Cartesian3.fromDegreesArray([tl[0], tl[1], tr[0], tr[1], br[0], br[1], bl[0], bl[1]]),
          fill: p.fill !== undefined ? p.fill : false,
          material: p.material,
          outline: p.outline,
          outlineColor: p.outlineColor,
          outlineWidth: p.outlineWidth,
          heightReference: p.heightReference,
          classificationType: p.classificationType,
        };
      }

      polygonEntity = entities.add({
        polygon: polygonOptions,
        properties: new Cesium.PropertyBag({
          id: new Cesium.ConstantProperty(polygonId),
          ...(creator && {
            creator: new Cesium.ConstantProperty(creator),
          }),
          ...(poleId !== null && {
            traffic_light_pole_id: new Cesium.ConstantProperty(poleId),
          }),
        }),
      });

      const polygonProps = {
        kind: "traffic_light_box",
        entityId: polygonEntity.id,
        id: polygonId,
      };

      if (poleId !== null) {
        polygonProps.traffic_light_pole_id = poleId;
      }

      polygonFeature = {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [[tl, tr, br, bl, tl]],
        },
        properties: polygonProps,
      };
    }

    if (!polygonFeature && !pointFeature) {
      return;
    }

    onCreateFeatures(polygonFeature, pointFeature);
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  return true;
}
